import { computed } from "vue";
import { useRoute } from "vue-router/composables";
import { isNullish, SubscribedNavbarButton } from "@tinyurldev/tu-ui-vue";

import { useLegacyStore, useTranslation } from "@Resources/js/composables";
import { useCurrentOrganization } from "@Modules/teams/composables/useCurrentOrganization";
import { XShoppingCartStore } from "@Modules/shoppingcart/Store/XShoppingCartStore";

export const availableButtons = {
  HOME: "home",
  HELP: "help",
  SETTING_DOMAIN: "setting-domain",
  KIOSK: "kiosk",
  RETURN_KIOSK: "return-kiosk",
  NOTIFICATION: "notification",
  SHORTEN: "shorten",
  SHOPPING_CART: "shopping-cart",
};

/**
 * The navigation buttons composable.
 */
export default function useNavigationButtons() {
  const route = useRoute();
  const store = useLegacyStore();
  const { __ } = useTranslation();
  const { currentOrganization } = useCurrentOrganization();
  const cartStore = XShoppingCartStore();

  const isInOrganization = computed(() => !isNullish(currentOrganization.value));
  const unreadNotificationsTotal = computed<number>(() => {
    const notifications = store?.getters.unreadNotificationsCount ?? 0;
    const announcements = store?.getters.unreadAnnouncementsCount ?? 0;

    return notifications + announcements;
  });

  const homeButton = computed<SubscribedNavbarButton>(() => {
    const isPaidUser = store?.getters.isUserSubscribed;
    const title = (isPaidUser || isInOrganization.value) ? __("navbar.dashboard_button") : __("navbar.home_button");
    const icon = (isPaidUser || isInOrganization.value) ? "fa-solid fa-server" : "fa-solid fa-house";
    const destination = isInOrganization.value ? "org.team" : "homepage";

    return {
      id: availableButtons.HOME,
      title: title,
      iconClass: icon,
      as: "router-link",
      tooltip: title,
      attributes: {
        to: {
          name: destination,
          ...( isInOrganization.value && {
            params: {
              org: currentOrganization.value?.slug,
              team: currentOrganization.value?.default_team_slug,
            },
          })
        },
        exactActiveClass: "active",
      },
    };
  });

  const helpButton = computed<SubscribedNavbarButton>(() => {
    const title = __("navbar.help_button");

    return {
      id: availableButtons.HELP,
      title: title,
      iconClass: "fa-solid fa-question",
      as: "router-link",
      tooltip: title,
      attributes: {
        to: { name: "help" },
        exactActiveClass: "active",
      },
    };
  });

  const domainSettingsButton = computed<SubscribedNavbarButton>(() => {
    const title = __("navbar.setting_domain_button");
    const destination = isInOrganization.value ? "org.settings.profile.domains" : "settings.brandedDomains";

    return {
      id: availableButtons.SETTING_DOMAIN,
      title: title,
      iconClass: "fa-solid fa-globe",
      as: "router-link",
      tooltip: title,
      attributes: {
        to: { name: destination },
        exactActiveClass: "active",
      },
    };
  });

  const kioskButton = computed<SubscribedNavbarButton>(() => {
    const title = __("navbar.kiosk_button");

    return {
      id: availableButtons.KIOSK,
      title: title,
      iconClass: "fa-solid fa-address-card",
      as: "router-link",
      tooltip: title,
      attributes: {
        to: { name: "kiosk" },
        exactActiveClass: "active",
      },
    };
  });

    const returnKioskButton = computed<SubscribedNavbarButton>(() => {
      const title = __("navbar.return_kiosk_button");

      return {
        id: availableButtons.RETURN_KIOSK,
        title: title,
        iconClass: "fa-solid fa-arrow-right-long",
        as: "a",
        tooltip: title,
        attributes: {
          href: "/app/kiosk/user/stop-impersonating"
        }
      };
    });


  const notificationButton = computed<SubscribedNavbarButton>(() => {
    const title = __("navbar.notification_button");

    return {
      id: availableButtons.NOTIFICATION,
      title: title,
      iconClass: "fa-solid fa-bell",
      tooltip: title,
      ...(unreadNotificationsTotal.value > 0 && { badge: `${unreadNotificationsTotal.value}` }),
    };
  });

  const shoppingCartButton = computed<SubscribedNavbarButton>(() => {
    const title = __("navbar.shopping_cart_button");
    const shoppingCartLink = route.name === "purchase.domain"
      ? "purchase.domain.cart"
      : "shopping-cart";

    return {
      id: availableButtons.SHOPPING_CART,
      title: title,
      iconClass: "fa-solid fa-shopping-cart",
      as: "router-link",
      tooltip: title,
      ...(cartStore.quantity > 0 && { badge: `${cartStore.quantity}` }),
      attributes: { to: { name: shoppingCartLink }, exactActiveClass: "active" },
    };
  });

  return {
    homeButton,
    helpButton,
    kioskButton,
    returnKioskButton,
    notificationButton,
    shoppingCartButton,
    domainSettingsButton,
  };
}
